import React from 'react';
import { Formik, Form } from 'formik';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import AuthLayout from '../auth/AuthLayout';
import { Button } from '../ui/button.tsx';
import { validateForm } from './validation/employeeSchema';
import { steps, renderFormStep } from './steps/FormSteps';
import { useCreateStaff } from '../../api/staff/staffQueries.js';
import { createTemplateNotification } from '../../api/notifications/staffNotificationApi';

export default function AddEmployeeForm({ isOpen, onClose }) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(1);
  const navigate = useNavigate();
  const { mutate: createStaff } = useCreateStaff();

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      setIsLoading(true);

      // Ensure date is in ISO format
      const formattedDob = new Date(values.dob).toISOString();

      const staffData = {
        firstName: values.firstName,
        lastName: values.lastName,
        address: values.address,
        dob: formattedDob,
        email: values.email,
      };

      await createStaff(staffData);

      // Show success toast and create notification
      toast.success('Staff member added successfully');
      await createTemplateNotification('staffAdded');

      navigate('/team'); 
    } catch (error) {
      console.error('Failed to add staff:', error);

      if (error.message.includes('Unauthorized')) {
        toast.error('Please login to add staff members');
        navigate('/login', { state: { from: '/add-staff' } });
      } else if (error.message.includes('Invalid')) {
        toast.error(error.message);
        setErrors({ submit: error.message });
      } else {
        toast.error('Failed to add staff member. Please try again.');
        setErrors({ submit: error.message });
      }
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  // Function to validate current step fields
  const validateStep = (values) => {
    const errors = validateForm(values);
    const currentFields = steps[currentStep - 1].fields;
    const currentErrors = {};

    // Only check errors for current step fields
    currentFields.forEach((field) => {
      if (errors[field]) {
        currentErrors[field] = errors[field];
      }
    });

    return currentErrors;
  };

  return (
    <AuthLayout>
      <div className="mx-auto flex w-full flex-col justify-center space-y-4 sm:w-[700px]">
        <div className="bg-white p-8 rounded-2xl shadow-sm relative">
          {/* Progress Bar */}
          <div className="absolute top-6 right-6 flex items-center gap-2">
            <div className="w-48 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#7C3AED] transition-all duration-300 rounded-full"
                style={{
                  width: `${(currentStep / steps.length) * 100}%`,
                }}
              />
            </div>
            <span className="text-sm text-muted-foreground">
              Step {currentStep}/{steps.length}
            </span>
          </div>

          {/* Form Title */}
          <div className="mb-6 mt-2">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              {steps[currentStep - 1].name}
            </h1>
          </div>

          <Formik
            initialValues={{
              firstName: '',
              lastName: '',
              email: '',
              dob: '',
              address: '',
            }}
            validate={validateStep}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              isSubmitting,
              values,
              setFieldValue,
              validateForm,
            }) => (
              <Form className="space-y-4">
                {renderFormStep(
                  currentStep,
                  errors,
                  touched,
                  values,
                  setFieldValue,
                  isLoading
                )}

                {/* Show any submit errors */}
                {errors.submit && (
                  <div className="text-red-500 text-sm mt-2">
                    {errors.submit}
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex gap-3 pt-4">
                  {currentStep > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-12 text-base font-semibold"
                      onClick={() => setCurrentStep((step) => step - 1)}
                      disabled={isLoading}
                    >
                      Previous
                    </Button>
                  )}
                  <Button
                    type={currentStep === steps.length ? 'submit' : 'button'}
                    className="w-full h-12 text-base font-semibold"
                    disabled={isLoading}
                    onClick={async () => {
                      if (currentStep < steps.length) {
                        // Validate current step before proceeding
                        const stepErrors = await validateForm(values);
                        const currentFields = steps[currentStep - 1].fields;
                        const hasStepErrors = currentFields.some(
                          (field) => stepErrors[field]
                        );

                        if (!hasStepErrors) {
                          setCurrentStep((step) => step + 1);
                        }
                      }
                    }}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {currentStep === steps.length
                          ? 'Adding Staff...'
                          : 'Next'}
                      </>
                    ) : currentStep === steps.length ? (
                      'Add Staff Member'
                    ) : (
                      'Next'
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </AuthLayout>
  );
}
